import dayjs from 'dayjs';
import { graphql, useFragment, useMutation } from 'react-relay';
import type { EventCardFragment$key } from './__generated__/EventCardFragment.graphql';
import { CoreEventTimeCard, useToast } from '@vitvio/design-system';
import type { TranslationValue } from '@vitvio/design-system';
import type { EventCardCreateMutation } from './__generated__/EventCardCreateMutation.graphql';
import type { EventCardUpdateMutation } from './__generated__/EventCardUpdateMutation.graphql';
import { useTranslation } from '@vitvio/locales';
import { getLoggerSafe } from '@vitvio/logging';
import type { EventCategoryEnum } from './__generated__/EventPanel.graphql';
import { useUserContext } from '@vitvio/user-context';
import { fromZonedTime, toZonedTime } from '@vitvio/helpers';

const fragment = graphql`
  fragment EventCardFragment on Event {
    id
    happenedAt
  }
`;

const createMutation = graphql`
  mutation EventCardCreateMutation($input: EventCreateInput!) {
    createEvent(input: $input) {
      node {
        id
        happenedAt
        type {
          id
          name
        }
      }
    }
  }
`;

const updateMutation = graphql`
  mutation EventCardUpdateMutation($input: EventUpdateInput!) {
    updateEvent(input: $input) {
      node {
        id
        happenedAt
      }
    }
  }
`;

interface Props {
  eventRef: EventCardFragment$key | undefined;
  name: string;
  nameI18nKey?: string;
  nameI18nParams?: TranslationValue;
  typeEventId: string;
  previousEventTime: string;
  nextEventTime: string;
  jobId: string;
  enabled: boolean;
  blockedReason?: string;
  blockedReasonParams?: TranslationValue;
  category: EventCategoryEnum | null;
}

export function EventCard(props: Props) {
  const {
    name,
    nameI18nKey,
    nameI18nParams,
    eventRef,
    typeEventId,
    jobId,
    enabled,
    blockedReason,
    blockedReasonParams,
    category,
    previousEventTime,
    nextEventTime,
  } = props;
  const { t } = useTranslation();
  const toast = useToast();
  const logger = getLoggerSafe();
  const { hospitalTimezone } = useUserContext();

  const data = useFragment(fragment, eventRef ?? null);
  const eventExists = !!data?.id;

  const [createEventCommit] = useMutation<EventCardCreateMutation>(createMutation);
  const [updateEventCommit] = useMutation<EventCardUpdateMutation>(updateMutation);

  // Display times in hospital timezone
  const value = data?.happenedAt ? toZonedTime(data.happenedAt, hospitalTimezone) : null;
  const now = toZonedTime(dayjs(), hospitalTimezone) ?? dayjs();

  const isPrimary = category != null;
  const prevPrimary = previousEventTime ? toZonedTime(previousEventTime, hospitalTimezone) : null;
  const nextPrimary = nextEventTime ? toZonedTime(nextEventTime, hospitalTimezone) : null;

  const startOfDay = (prevPrimary ?? nextPrimary ?? now).startOf('day');
  const min = isPrimary ? (prevPrimary ?? startOfDay) : null;
  const max = isPrimary ? (nextPrimary ?? now) : now;

  function payloadErrorsToError(errors: unknown) {
    if (!Array.isArray(errors) || errors.length === 0) return null;
    const messages = errors
      .map((e) => {
        if (e && typeof e === 'object' && 'message' in e) {
          return String((e as { message?: unknown }).message ?? '');
        }
        return String(e ?? '');
      })
      .map((m) => m.trim())
      .filter(Boolean);

    if (messages.length === 0) return new Error('Mutation failed');
    return new Error(messages.join('\n'));
  }

  // Current instant in UTC (same moment everywhere; display uses hospital TZ elsewhere)
  function getNowInUtc(): string {
    return dayjs().utc().toISOString();
  }

  async function handleCreateEvent(happenedAt?: string) {
    const time = happenedAt ?? getNowInUtc();
    await new Promise((resolve, reject) =>
      createEventCommit({
        variables: {
          input: {
            happenedAt: time,
            typeEventId,
            jobId,
          },
        },
        onCompleted: (response, errors) => {
          const err = payloadErrorsToError(errors);
          if (err) return reject(err);
          resolve(response);
        },
        onError: (error) => {
          reject(error);
        },
      }),
    );
  }

  async function handleUpdateEvent(happenedAt?: string) {
    if (!data?.id) return;
    const time = happenedAt ?? getNowInUtc();

    await new Promise((resolve, reject) =>
      updateEventCommit({
        variables: {
          input: {
            happenedAt: time,
            id: data.id,
          },
        },
        onCompleted: (response, errors) => {
          const err = payloadErrorsToError(errors);
          if (err) return reject(err);
          resolve(response);
        },
        onError: (error) => {
          reject(error);
        },
      }),
    );
  }

  function handleNowClick() {
    if (eventExists) {
      handleUpdateEvent().catch((error) => {
        logger?.error('Failed to update event:', error);
        toast({
          title: t('common:errors.eventUpdateFailed'),
          description: error instanceof Error ? error.message : String(error),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      });
      return;
    }
    handleCreateEvent().catch((error) => {
      logger?.error('Failed to create event:', error);
      toast({
        title: t('common:errors.eventCreateFailed'),
        description: error instanceof Error ? error.message : String(error),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    });
  }

  function handleChange(nextValue: dayjs.Dayjs | null) {
    if (!nextValue) return;

    // Convert the selected time (interpreted as hospital timezone) to UTC
    const utcTime = fromZonedTime(nextValue, hospitalTimezone);
    const iso = utcTime?.toISOString() ?? dayjs().utc().toISOString();

    if (eventExists) {
      handleUpdateEvent(iso).catch((error) => {
        logger?.error('Failed to update event:', error);
        toast({
          title: t('common:errors.eventUpdateFailed'),
          description: error instanceof Error ? error.message : String(error),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      });
      return;
    }
    handleCreateEvent(iso).catch((error) => {
      logger?.error('Failed to create event:', error);
      toast({
        title: t('common:errors.eventCreateFailed'),
        description: error instanceof Error ? error.message : String(error),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    });
  }

  /** API returns eventTypes.* or blocked.* suffixes; prepend platform:events. */
  function normalizeEventsKey(key: string | undefined): string | undefined {
    if (!key) return undefined;
    const trimmed = key.trim();
    if (!trimmed) return undefined;
    if (trimmed.includes(':')) return trimmed;
    return `platform:events.${trimmed}`;
  }

  const isDisabled = !enabled && !eventExists;
  const labelPath = normalizeEventsKey(nameI18nKey);
  const disabledMessagePath = normalizeEventsKey(blockedReason) ?? 'common:eventTimeCard.disabledMessage';

  return (
    <CoreEventTimeCard
      labelPath={labelPath}
      label={labelPath ? undefined : name}
      labelValue={nameI18nParams}
      value={value}
      onNowClick={handleNowClick}
      onChange={handleChange}
      minuteStep={1}
      isDisabled={isDisabled}
      disabledMessagePath={disabledMessagePath}
      disabledMessageValue={blockedReasonParams}
      min={min}
      max={max}
    />
  );
}
